import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../contexts/ToastContext';
import { setActiveTab } from '../../redux/slices/appSlice';
import { track } from '../../utils/analytics';

/**
 * A build runs for minutes while the user is elsewhere in the app. Mounted once
 * at the app root: when a build ends and the Automix tab isn't on screen, say
 * so — with a way back. (On the tab itself the view change is the feedback.)
 * Out of the app entirely, the main process posts a system notification
 * (automix/buildAttention.js); clicking it lands here too.
 */
const AutomixBuildWatcher = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    const phase = useSelector((s) => s.automix.phase);
    const set = useSelector((s) => s.automix.set);
    const onTab = useSelector((s) => s.app.activeTab === 'automix');
    const prevPhase = useRef(phase);
    const setRef = useRef(set);
    setRef.current = set;

    // back to the tab, which shows the finished set (or why it failed)
    const openFrom = (from) => {
        dispatch(setActiveTab('automix'));
        const s = setRef.current;
        if (s) track('automix_set_opened', { track_count: s.trackCount, duration_sec: Math.round(s.durationSec || 0), from });
    };

    useEffect(() => {
        const was = prevPhase.current;
        prevPhase.current = phase;
        if (was !== 'building' || onTab) return;
        const open = { label: 'Open', onClick: () => openFrom('toast') };
        if (phase === 'idle' && set) toast.showToast(`Your set “${set.name}” is ready`, 'success', 8000, open);
        else if (phase === 'failed') toast.showToast('Your set couldn’t be built', 'error', 8000, open);
    }, [phase, set, onTab, toast, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => window.electron?.automix?.onOpenSet?.(() => openFrom('notification')), []); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
};

export default AutomixBuildWatcher;
